'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { MapPin, Loader2, X, Building2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { API_BASE } from '@/lib/config';

// Location types for multi-select
export interface CityLocation {
  type: 'city';
  id: string;
  name: string;
  displayName: string;
  state: string;
}

export interface AreaLocation {
  type: 'area';
  pincode: string;
  officeName: string;
  district: string;
  state: string;
  displayName: string;
}

export type LocationItem = CityLocation | AreaLocation;

interface LocationMultiSelectProps {
  value: LocationItem[];
  onChange: (locations: LocationItem[]) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  maxSelections?: number;
  /** If true, only allow city selection (no specific areas) */
  citiesOnly?: boolean;
}

export function LocationMultiSelect({
  value = [],
  onChange,
  placeholder = 'Search cities...',
  className,
  required = false,
  disabled = false,
  maxSelections = 5,
  citiesOnly = true,  // Default to cities-only for preferred locations
}: LocationMultiSelectProps) {
  const [query, setQuery] = useState('');
  const [cities, setCities] = useState<CityLocation[]>([]);
  const [areas, setAreas] = useState<AreaLocation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [popularCities, setPopularCities] = useState<CityLocation[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load popular cities on mount
  useEffect(() => {
    const loadPopularCities = async () => {
      try {
        const response = await fetch(`${API_BASE}/pincodes/cities?isItHub=true&limit=8`);
        const data = await response.json();
        if (data.success && data.data) {
          setPopularCities(data.data.map((city: { id: string; name: string; displayName: string; state: string }) => ({
            type: 'city' as const,
            id: city.id,
            name: city.name,
            displayName: city.displayName,
            state: city.state
          })));
        }
      } catch {
        // Failed to load popular cities - non-critical
      }
    };
    loadPopularCities();
  }, []);

  // Search locations (cities + areas)
  const searchLocations = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setCities([]);
      setAreas([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/pincodes/locations/search?q=${encodeURIComponent(searchQuery)}`);
      const data = await response.json();

      if (data.success && data.data) {
        // Map cities
        const cityResults = (data.data.cities || []).map((city: { id: string; name: string; displayName: string; state: string }) => ({
          type: 'city' as const,
          id: city.id,
          name: city.name,
          displayName: city.displayName,
          state: city.state
        }));
        setCities(cityResults);

        // Map areas (skip if citiesOnly mode)
        if (!citiesOnly) {
          const areaResults = (data.data.areas || []).map((area: { pincode: string; officeName: string; district: string; state: string }) => ({
            type: 'area' as const,
            pincode: area.pincode,
            officeName: area.officeName,
            district: area.district,
            state: area.state,
            displayName: `${area.officeName}, ${area.district}`
          }));
          setAreas(areaResults);
        } else {
          setAreas([]);
        }
      }
    } catch {
      // Search failed silently - user can retry
      setCities([]);
      setAreas([]);
    } finally {
      setIsLoading(false);
    }
  }, [citiesOnly]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (query) {
        searchLocations(query);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchLocations]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if location is already selected
  const isSelected = (location: LocationItem): boolean => {
    return value.some(v => {
      if (v.type === 'city' && location.type === 'city') {
        return v.id === location.id;
      }
      if (v.type === 'area' && location.type === 'area') {
        return v.pincode === location.pincode;
      }
      return false;
    });
  };

  // Add location
  const addLocation = (location: LocationItem) => {
    if (value.length >= maxSelections) {
      return;
    }
    if (!isSelected(location)) {
      onChange([...value, location]);
    }
    setQuery('');
    setCities([]);
    setAreas([]);
    inputRef.current?.focus();
  };

  // Remove location
  const removeLocation = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  // Get display label for a location
  const getDisplayLabel = (location: LocationItem): string => {
    if (location.type === 'city') {
      return location.name;
    }
    return `${location.officeName}, ${location.district}`;
  };

  const hasResults = cities.length > 0 || areas.length > 0;

  return (
    <div className={cn('space-y-3', className)}>
      {/* Selected Locations */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((location, index) => (
            <div
              key={location.type === 'city' ? location.id : location.pincode}
              className={cn(
                'flex items-center gap-1.5 rounded-full px-3 py-1 text-sm',
                location.type === 'city'
                  ? 'bg-primary/10 text-primary border border-primary/20'
                  : 'bg-secondary/50 text-secondary-foreground border border-secondary'
              )}
            >
              {location.type === 'city' ? (
                <Building2 className="h-3 w-3" />
              ) : (
                <MapPin className="h-3 w-3" />
              )}
              <span>{getDisplayLabel(location)}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeLocation(index)}
                  className="ml-1 hover:text-destructive"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Search Input */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setShowDropdown(true);
          }}
          onFocus={() => setShowDropdown(true)}
          placeholder={value.length >= maxSelections ? `Max ${maxSelections} locations` : placeholder}
          disabled={disabled || value.length >= maxSelections}
          className={cn(
            'w-full rounded-lg border border-input bg-background py-2.5 pl-10 pr-4',
            'text-sm text-foreground placeholder:text-muted-foreground',
            'focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary',
            'disabled:bg-muted disabled:cursor-not-allowed'
          )}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {/* Dropdown */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="rounded-lg border border-border bg-popover shadow-lg max-h-80 overflow-y-auto"
        >
          {/* No search query - show popular cities */}
          {!query && popularCities.length > 0 && (
            <div className="p-3">
              <p className="text-xs font-medium text-muted-foreground mb-2">Popular IT Cities</p>
              <div className="flex flex-wrap gap-2">
                {popularCities.map((city) => (
                  <button
                    key={city.id}
                    type="button"
                    onClick={() => addLocation(city)}
                    disabled={isSelected(city)}
                    className={cn(
                      'rounded-full px-3 py-1 text-sm border transition-colors',
                      isSelected(city)
                        ? 'bg-muted text-muted-foreground cursor-not-allowed'
                        : 'bg-background hover:bg-primary/10 hover:border-primary/30'
                    )}
                  >
                    {city.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search results */}
          {query && hasResults && (
            <>
              {/* Cities section */}
              {cities.length > 0 && (
                <div className="p-2 border-b border-border">
                  <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Cities (All Areas)</p>
                  {cities.map((city) => (
                    <button
                      key={city.id}
                      type="button"
                      onClick={() => addLocation(city)}
                      disabled={isSelected(city)}
                      className={cn(
                        'flex w-full items-center gap-2 px-2 py-2 text-left rounded-md',
                        isSelected(city)
                          ? 'bg-muted text-muted-foreground cursor-not-allowed'
                          : 'hover:bg-accent'
                      )}
                    >
                      <Building2 className="h-4 w-4 text-primary" />
                      <div>
                        <div className="font-medium">{city.displayName}</div>
                        <div className="text-xs text-muted-foreground">{city.state}</div>
                      </div>
                      {isSelected(city) && (
                        <span className="ml-auto text-xs text-muted-foreground">Added</span>
                      )}
                    </button>
                  ))}
                </div>
              )}

              {/* Areas section */}
              {areas.length > 0 && (
                <div className="p-2">
                  <p className="text-xs font-medium text-muted-foreground px-2 mb-1">Specific Areas</p>
                  {areas.slice(0, 5).map((area, index) => (
                    <button
                      key={`${area.pincode}-${index}`}
                      type="button"
                      onClick={() => addLocation(area)}
                      disabled={isSelected(area)}
                      className={cn(
                        'flex w-full items-center gap-2 px-2 py-2 text-left rounded-md',
                        isSelected(area)
                          ? 'bg-muted text-muted-foreground cursor-not-allowed'
                          : 'hover:bg-accent'
                      )}
                    >
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">{area.officeName}</div>
                        <div className="text-xs text-muted-foreground">
                          {area.district}, {area.state} - {area.pincode}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}

          {/* No results */}
          {query.length >= 2 && !hasResults && !isLoading && (
            <div className="p-4 text-center text-sm text-muted-foreground">
              No locations found for &quot;{query}&quot;
            </div>
          )}

          {/* Hint */}
          {!query && (
            <div className="p-3 border-t border-border text-xs text-muted-foreground">
              {citiesOnly ? 'Search by city name' : 'Search by city name, area name, or pincode'}
            </div>
          )}
        </div>
      )}

      {/* Counter */}
      {required && value.length === 0 && (
        <p className="text-xs text-destructive">Please select at least one location</p>
      )}
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {value.length} of {maxSelections} locations selected
        </p>
      )}
    </div>
  );
}
